// How a translation catalogue is kept: every entry of the English catalogue, key for key, each marked
// machine-drafted until a person reviews it. A key missing from a translation, or one English does not
// have, fails `pnpm typecheck`; `pnpm i18n:report` lists the entries still machine-drafted.

/** One translated entry: its words (or the function that words it), and whether a person has reviewed it. */
export interface Entry<V> {
  readonly value: V;
  readonly reviewed: boolean;
}

/** A translation of `Words`: an entry for each of its keys. */
export type Catalogue<Words> = { readonly [K in keyof Words]: Entry<Words[K]> };

/** Machine-drafted: shown as it is until a person reviews it. */
export const draft = <V>(value: V): Entry<V> => ({ value, reviewed: false });

/** Reviewed by a person who reads the language. */
export const reviewed = <V>(value: V): Entry<V> => ({ value, reviewed: true });

/** The words a translation shows, key for key. */
export function wordsOf<Words extends object>(catalogue: Catalogue<Words>): Words {
  const words = {} as Words;
  for (const key of Object.keys(catalogue) as (keyof Words)[]) words[key] = catalogue[key].value;
  return words;
}

/** The keys whose entries are still machine-drafted, in catalogue order. */
export const unreviewed = <Words extends object>(catalogue: Catalogue<Words>): (keyof Words)[] =>
  (Object.keys(catalogue) as (keyof Words)[]).filter((key) => !catalogue[key].reviewed);
